import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { rpaRepository as repo } from '../../../lib/repositories/rpa.repository';
import type { KenhCauHinh, KenhThongBao, QuyTacThongBao, TaiKhoanGui, ThanQuyTacThongBao, ThanTaiKhoanGui } from '../types';
import { KHOA_RPA, thuLai } from './useRpa';

export function useQuyTacThongBao(bat = true) {
  return useQuery<QuyTacThongBao[], ApiError>({
    queryKey: KHOA_RPA.quyTacThongBao,
    queryFn: () => repo.quyTacThongBao(),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

/** Tình trạng cấu hình từng kênh: đổi theo tài khoản gửi đang bật, nên tài khoản gửi đổi là làm mới. */
export function useKenhThongBao(bat = true) {
  return useQuery<KenhCauHinh[], ApiError>({
    queryKey: KHOA_RPA.kenhThongBao,
    queryFn: () => repo.kenhThongBao(),
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Máy chủ cho đọc bằng `rpa:tai-khoan-gui:read` hoặc `rpa:thong-bao:update` (form quy tắc chọn tài khoản gửi). */
export function useTaiKhoanGui(bat = true) {
  return useQuery<TaiKhoanGui[], ApiError>({
    queryKey: KHOA_RPA.taiKhoanGui,
    queryFn: () => repo.taiKhoanGui(),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

function useLamMoiThongBao() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: KHOA_RPA.thongBao });
}

export function useLuuQuyTac() {
  const lamMoi = useLamMoiThongBao();
  return useMutation<QuyTacThongBao, ApiError, { id: string | null; than: ThanQuyTacThongBao }>({
    mutationFn: ({ id, than }) => (id ? repo.suaQuyTac(id, than) : repo.taoQuyTac(than)),
    onSettled: lamMoi,
  });
}

/** Bật/tắt cả quy tắc (PATCH `enabled`) hoặc riêng một Project với quy tắc kế thừa. */
export function useBatQuyTac() {
  const lamMoi = useLamMoiThongBao();
  return useMutation<QuyTacThongBao, ApiError, { id: string; enabled: boolean; project: string }>({
    mutationFn: ({ id, enabled, project }) =>
      project ? repo.batQuyTacChoProject(id, project, enabled) : repo.suaQuyTac(id, { enabled }),
    onSettled: lamMoi,
  });
}

export function useXoaQuyTac() {
  const lamMoi = useLamMoiThongBao();
  return useMutation<void, ApiError, string>({ mutationFn: (id) => repo.xoaQuyTac(id), onSettled: lamMoi });
}

export function useGuiThuKenh() {
  return useMutation<{ success: true }, ApiError, { channel: KenhThongBao; target: string; accountId: string }>({
    mutationFn: (than) => repo.guiThuKenh(than),
  });
}

export function useLuuTaiKhoanGui() {
  const lamMoi = useLamMoiThongBao();
  return useMutation<TaiKhoanGui, ApiError, { id: string | null; than: ThanTaiKhoanGui }>({
    mutationFn: ({ id, than }) => (id ? repo.suaTaiKhoanGui(id, than) : repo.taoTaiKhoanGui(than)),
    onSettled: lamMoi,
  });
}

export function useXoaTaiKhoanGui() {
  const lamMoi = useLamMoiThongBao();
  return useMutation<void, ApiError, string>({ mutationFn: (id) => repo.xoaTaiKhoanGui(id), onSettled: lamMoi });
}

export function useGuiThuTaiKhoan() {
  return useMutation<{ success: true }, ApiError, { id?: string; account: ThanTaiKhoanGui; to?: string }>({
    mutationFn: (than) => repo.guiThuTaiKhoan(than),
  });
}
